import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../lib/api';
import {
  createProjectResource,
  createProjectResourceFolder,
  deleteProjectResource,
  deleteProjectResourceFolder,
  getProject,
  getProjectResourcesTree,
  listProjects,
  type ProjectFilters,
  type ProjectResourceCategory,
} from '../lib/projectsApi';
import { useActiveCompanyId } from '../store/useCompanyStore';

// Projects are markdown-first (CLE-41): most mutations happen through the AI
// assistant (KB markdown -> sync). The exceptions below are the manual resource
// upload/delete endpoints (contracts stay markdown-first server-side).

export function useProjects(filters: ProjectFilters = {}) {
  // Global firm switcher (multi-company): threaded internally so pages don't
  // have to pass it. An explicit filters.companyId wins.
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  const merged: ProjectFilters = { companyId: activeCompanyId, ...filters };
  return useQuery({
    queryKey: ['projects', merged],
    queryFn: () => listProjects(merged),
  });
}

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: ['project', id],
    queryFn: () => getProject(id as string),
    enabled: Boolean(id),
  });
}

// Resources DMS tree (CLE-48): Explorer-style directory tree assembled from the
// project's invoices and resources.
export function useProjectResourcesTree(id: string | undefined) {
  return useQuery({
    queryKey: ['project-resources-tree', id],
    queryFn: () => getProjectResourcesTree(id as string),
    enabled: Boolean(id),
  });
}

// Manual resource upload/delete (Contract / Design / Altele). Both refresh the
// DMS tree and the project detail (its `resources` list embeds the same rows).
function invalidateResources(qc: ReturnType<typeof useQueryClient>, id: string) {
  void qc.invalidateQueries({ queryKey: ['project-resources-tree', id] });
  void qc.invalidateQueries({ queryKey: ['project', id] });
}

export function useCreateProjectResource(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      category: ProjectResourceCategory;
      s3Key: string;
      description?: string;
      fileType?: string;
      fileSize?: number;
      folderPath?: string;
    }) => createProjectResource(id, input),
    onSuccess: () => invalidateResources(qc, id),
  });
}

export function useCreateProjectResourceFolder(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (path: string) => createProjectResourceFolder(id, path),
    onSuccess: () => invalidateResources(qc, id),
  });
}

/**
 * Create many folders at once (a whole-folder upload), skipping ones that
 * already exist, and refresh the tree once at the end rather than per folder.
 * The server creates missing parents, so only the deepest paths are sent.
 */
export function useEnsureProjectResourceFolders(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (paths: string[]) => {
      const leaves = paths.filter((p) => !paths.some((q) => q.startsWith(`${p}/`)));
      for (const path of leaves) {
        try {
          await createProjectResourceFolder(id, path);
        } catch (err) {
          if (!(err instanceof ApiError && err.code === 'FOLDER_EXISTS')) throw err;
        }
      }
    },
    onSettled: () => invalidateResources(qc, id),
  });
}

export function useDeleteProjectResourceFolder(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (path: string) => deleteProjectResourceFolder(id, path),
    onSuccess: () => invalidateResources(qc, id),
  });
}

export function useDeleteProjectResource(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (resourceId: string) => deleteProjectResource(id, resourceId),
    onSuccess: () => invalidateResources(qc, id),
  });
}
